import React, { useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Row, Col, ButtonGroup, Button, ButtonDropdown, DropdownToggle, DropdownMenu, DropdownItem, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import NavigationSettings from './NavigationSettings/NavigationSettings';
import ScreensSettings from './ScreensSettings/ScreensSettings';
import FeaturesSettings from './FeaturesSettings/FeaturesSettings';
import ThemeSettings from './ThemeSettings/ThemeSettings';
import ConfigSettings from './ConfigSettings/ConfigSettings';
import MobileAppPreviewDock from './MobileAppPreviewDock/MobileAppPreviewDock';
import ItemsList from '../ItemsList/ItemsList';
import { copyMobileSettings } from '../../redux';
import QRCode from "react-qr-code";
import { qrCodeContent as buildQrCodeContent } from './qrCodeContent';
import { VIEWER_STORE_LINKS } from './viewerStoreLinks';
import {
    MOBILE_ENVS,
    mobile_settings_path,
    parse_mobile_settings_route,
} from './mobileRouteUtils';

import './SettingsMobileField.css';

const MODAL_VIEW_CONFIG = 'config';
const MODAL_VIEW_IOS = 'ios';
const MODAL_VIEW_ANDROID = 'android';

const SettingsMobileField = (props) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalView, setModalView] = useState(MODAL_VIEW_CONFIG);

    const { t }       = useTranslation();
    const dispatch    = useDispatch();
    const navigate    = useNavigate();
    const { settingId, '*': route_splat } = useParams();
    const { setting } = useSelector(state => state.settings);
    const user        = useSelector(state => state.auth.user);
    // Prefer live Redux catalogs over props baked by Setting.propertyForm:
    // deep links can mount Config before the first list response arrives, and
    // props alone may stay stale until another screen triggers a re-render.
    const { salepoints } = useSelector(state => state.salepoints);
    const { tickettypes } = useSelector(state => state.tickettypes);
    const { articles } = useSelector(state => state.articles);
    const { articlecategories } = useSelector(state => state.articlecategories);
    const { post_categories } = useSelector(state => state.post_categories);
    const { places } = useSelector(state => state.places);

    const catalogProps = {
        ...props,
        salepoints: salepoints ?? props.salepoints,
        tickettypes: tickettypes ?? props.tickettypes,
        articles: articles ?? props.articles,
        articleCategories: articlecategories ?? props.articleCategories ?? props.articlecategories,
        postCategories: post_categories ?? props.postCategories,
        places: places ?? props.places,
    };

    const {
        env,
        section: activeSection,
        screen_id: activeScreen,
    } = parse_mobile_settings_route(route_splat);

    const go_to_mobile = useCallback((next_env, next_section, next_screen = null) => {
        if (!settingId)
            return;
        navigate(
            mobile_settings_path(settingId, next_env, next_section, next_screen),
            { replace: true }
        );
    }, [navigate, settingId]);

    // Canonicalize incomplete / invalid mobile URLs (e.g. bare …/mobile).
    useEffect(() => {
        if (!settingId)
            return;
        const canonical = mobile_settings_path(
            settingId,
            env,
            activeSection,
            activeSection === 'screens' ? (activeScreen || 'home') : null
        );
        const current = `/settings/edit/${encodeURIComponent(settingId)}/${
            route_splat != null ? String(route_splat).replace(/\/+$/, '') : 'mobile'
        }`;
        if (current !== canonical)
            navigate(canonical, { replace: true });
    }, [settingId, env, activeSection, activeScreen, route_splat, navigate]);

    const toggleDropdown = () => {
        setIsDropdownOpen(!isDropdownOpen);
    }

    const copySettings = (src, destination) => {
        dispatch(copyMobileSettings(src, destination));
    }

    const showModal = () => {
        setModalView(MODAL_VIEW_CONFIG);
        setIsModalOpen(true);
    };
    const hideModal = () => {
        setIsModalOpen(false);
        setModalView(MODAL_VIEW_CONFIG);
    };

    const icon = section => {
        switch (section.id) {
            case 'navigation':
                return 'bars';
            case 'screens':
                return 'tablet';
            case 'features':
                return 'puzzle-piece';
            case 'theme':
                return 'paint-brush';
            case 'config':
                return 'gear';
            default:
                return 'circle-o';
        }
    }

    const sections = [
        { id: 'navigation' },
        { id: 'screens' },
        { id: 'features' },
        { id: 'theme' },
        { id: 'config' },
    ];

    const items = sections.map(section => ({
        key: section.id,
        selection: section.id,
        title: t(`settings.mobile.sections.${section.id}`),
        icon: icon(section),
        active: section.id === activeSection,
        depth: 0
    }));

    const qrCodeContent = () => buildQrCodeContent(
        env,
        setting?.mobile?.[env],
        user?.tkt_instance?.engine_url
    );

    return (
        <div className="SettingsMobileField">
            <br/>
            <div className="wrapper-0">
                <Row>
                    <Col>
                        <div className="d-flex justify-content-between">
                            <ButtonGroup>
                                <Button onClick={() => go_to_mobile('production', activeSection, activeScreen) } color={ env === 'production' ? 'info' : 'secondary' }>
                                    <i className="fa fa-rocket"></i>{' '}
                                    { t('settings.mobile.production_env') }
                                </Button>
                                <Button onClick={() => go_to_mobile('preprod', activeSection, activeScreen) } color={ env === 'preprod' ? 'info' : 'secondary' }>
                                    <i className="fa fa-comments"></i>{' '}
                                    { t('settings.mobile.preprod_env') }
                                </Button>
                                <Button onClick={() => go_to_mobile('testing', activeSection, activeScreen) } color={ env === 'testing' ? 'info' : 'secondary' }>
                                    <i className="fa fa-pencil"></i>{' '}
                                    { t('settings.mobile.testing_env') }
                                </Button>
                                <ButtonDropdown
                                    toggle={toggleDropdown}
                                    isOpen={ isDropdownOpen }
                                >
                                    <DropdownToggle color='secondary' split className="no-border-after">
                                        <i className="fa fa-bars"></i>{' '}
                                    </DropdownToggle>
                                    <DropdownMenu right>
                                        <DropdownItem header>
                                            <strong>{ t('settings.mobile.copy_testing') }</strong>
                                        </DropdownItem>
                                        <DropdownItem onClick={ () => copySettings('testing', 'preprod') } color="link">
                                            <i className="fa fa-arrow-right"></i>{' '}
                                            { t('settings.mobile.to_preprod') }
                                        </DropdownItem>
                                        <DropdownItem onClick={ () => copySettings('testing', 'production') } color="link">
                                            <i className="fa fa-arrow-right"></i>{' '}
                                            { t('settings.mobile.to_production') }
                                        </DropdownItem>
                                        <DropdownItem header>
                                            <strong>{ t('settings.mobile.copy_preprod') }</strong>
                                        </DropdownItem>
                                        <DropdownItem onClick={ () => copySettings('preprod', 'testing') } color="link">
                                            <i className="fa fa-arrow-right"></i>{' '}
                                            { t('settings.mobile.to_testing') }
                                        </DropdownItem>
                                        <DropdownItem onClick={ () => copySettings('preprod', 'production') } color="link">
                                            <i className="fa fa-arrow-right"></i>{' '}
                                            { t('settings.mobile.to_production') }
                                        </DropdownItem>
                                        <DropdownItem header>
                                            <strong>{ t('settings.mobile.copy_production') }</strong>
                                        </DropdownItem>
                                        <DropdownItem onClick={ () => copySettings('production', 'preprod') } color="link">
                                            <i className="fa fa-arrow-right"></i>{' '}
                                            { t('settings.mobile.to_preprod') }
                                        </DropdownItem>
                                        <DropdownItem onClick={ () => copySettings('production', 'testing') } color="link">
                                            <i className="fa fa-arrow-right"></i>{' '}
                                            { t('settings.mobile.to_testing') }
                                        </DropdownItem>
                                    </DropdownMenu>
                                </ButtonDropdown>
                            </ButtonGroup>
                            <Button onClick={ showModal } color="info" className="ml-5">
                                <i className="fa fa-mobile"></i>{' '}
                                { t('settings.mobile.test_on_real_device') }
                            </Button>
                        </div>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <ItemsList
                            horizontal
                            items={ items }
                            onSelect={ (section_id) => go_to_mobile(
                                env,
                                section_id,
                                section_id === 'screens' ? (activeScreen || 'home') : null
                            ) }
                        />
                    </Col>
                </Row>
                <Row>
                    <Col>
                    {/*
                      Keep all three env trees mounted (d-none when inactive).
                      A single instance whose Field names switch with `env`
                      would rename redux-form Fields and drop unsaved edits.
                    */}
                    { MOBILE_ENVS.map((panel_env) => (
                        <div
                            key={ panel_env }
                            className={ panel_env === env ? undefined : 'd-none' }
                            data-mobile-env={ panel_env }
                            aria-hidden={ panel_env !== env }
                        >
                            { activeSection === 'navigation' && (
                                <NavigationSettings { ...catalogProps } env={ panel_env } setting={ setting } />
                            )}
                            { activeSection === 'screens' && (
                                <ScreensSettings
                                    { ...catalogProps }
                                    env={ panel_env }
                                    setting={ setting }
                                    activeScreen={ activeScreen || 'home' }
                                    onSelectScreen={ (screen_id) => go_to_mobile(panel_env, 'screens', screen_id) }
                                />
                            )}
                            { activeSection === 'features' && (
                                <FeaturesSettings { ...catalogProps } env={ panel_env } setting={ setting } />
                            )}
                            { activeSection === 'theme' && (
                                <ThemeSettings { ...catalogProps } env={ panel_env } setting={ setting } />
                            )}
                            { activeSection === 'config' && (
                                <ConfigSettings { ...catalogProps } env={ panel_env } setting={ setting } />
                            )}
                        </div>
                    )) }
                    </Col>
                </Row>
            </div>
            <MobileAppPreviewDock env={ env } setting={ setting } />
            <Modal
                isOpen={isModalOpen}
                toggle={hideModal}
                modalClassName="SettingsMobileField-test-modal"
            >
                <ModalHeader toggle={hideModal}>
                    <i className="fa fa-mobile"></i>{' '}
                    {t('settings.mobile.test_on_real_device')}
                    {' | '}
                    {t(`settings.mobile.${env}_env`)}
                </ModalHeader>
                <ModalBody>
                    <div className="d-flex justify-content-center mb-3">
                        <ButtonGroup>
                            <Button
                                color={ modalView === MODAL_VIEW_CONFIG ? 'info' : 'secondary' }
                                onClick={ () => setModalView(MODAL_VIEW_CONFIG) }
                            >
                                { t('settings.mobile.configure_app') }
                            </Button>
                            <Button
                                color={ modalView === MODAL_VIEW_IOS ? 'info' : 'secondary' }
                                onClick={ () => setModalView(MODAL_VIEW_IOS) }
                            >
                                <i className="fa fa-apple"></i>{' '}
                                { t('settings.mobile.install_on_ios') }
                            </Button>
                            <Button
                                color={ modalView === MODAL_VIEW_ANDROID ? 'info' : 'secondary' }
                                onClick={ () => setModalView(MODAL_VIEW_ANDROID) }
                            >
                                <i className="fa fa-android"></i>{' '}
                                { t('settings.mobile.install_on_android') }
                            </Button>
                        </ButtonGroup>
                    </div>
                    {modalView === MODAL_VIEW_CONFIG && (
                        <>
                            {!qrCodeContent() && (
                                <div className="m-3 text-center">{t('settings.mobile.first_configure_your_app')}</div>
                            )}
                            {qrCodeContent() && (
                                <div className="step">
                                    <div className="text">{t('settings.mobile.scan_this_qr_code')}</div>
                                    <div className="qr-code">
                                        <QRCode value={ qrCodeContent() } size={180} />
                                    </div>
                                </div>
                            )}
                            <div className="step step-hint mt-3">
                                <div className="text">
                                    <i className="fa fa-info-circle"></i>{' '}
                                    {t('settings.mobile.how_to_reset_the_app')}
                                </div>
                            </div>
                        </>
                    )}
                    {modalView === MODAL_VIEW_IOS && (
                        <>
                            <div className="step">
                                <div className="text">{t('settings.mobile.install_on_ios')}</div>
                                <div className="qr-code">
                                    <QRCode value={ VIEWER_STORE_LINKS.ios } size={180} />
                                </div>
                                <a
                                    className="btn btn-link store-link"
                                    href={ VIEWER_STORE_LINKS.ios }
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <i className="fa fa-external-link"></i>{' '}
                                    { t('settings.mobile.open_app_store') }
                                </a>
                            </div>
                            <div className="step step-hint mt-3">
                                <div className="text">
                                    <i className="fa fa-info-circle"></i>{' '}
                                    { t('settings.mobile.ios_unlisted_hint') }
                                </div>
                            </div>
                        </>
                    )}
                    {modalView === MODAL_VIEW_ANDROID && (
                        <div className="step">
                            <div className="text">{t('settings.mobile.install_on_android')}</div>
                            <div className="qr-code">
                                <QRCode value={ VIEWER_STORE_LINKS.android } size={180} />
                            </div>
                            <a
                                className="btn btn-link store-link"
                                href={ VIEWER_STORE_LINKS.android }
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <i className="fa fa-external-link"></i>{' '}
                                { t('settings.mobile.open_play_store') }
                            </a>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={hideModal}>
                        <i className="fa fa-close"></i>&nbsp;
                        {t('common.cancel')}
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
}

export default SettingsMobileField;
